import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { theme } from '../../styles/theme';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: ${theme.spacing.md};
  background-color: ${theme.colors.light};
`;

const LoginCard = styled(Card)`
  max-width: 400px;
  width: 100%;
`;

const LoginHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing.lg};
  
  h1 {
    font-size: ${theme.fontSizes.xxlarge};
    margin-bottom: ${theme.spacing.xs};
  }
  
  p {
    color: ${theme.colors.gray};
  }
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const Label = styled.label`
  font-size: ${theme.fontSizes.medium};
  font-weight: 600;
`;

const Input = styled.input`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.gray};
  border-radius: ${theme.radii.small};
  font-size: ${theme.fontSizes.medium};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const ErrorMessage = styled.p`
  color: ${theme.colors.danger};
  font-size: ${theme.fontSizes.small};
  margin: ${theme.spacing.xs} 0 0 0;
`;

const ForgotPassword = styled(Link)`
  font-size: ${theme.fontSizes.small};
  text-align: right;
  color: ${theme.colors.primary};
`;

const SignUpLink = styled.div`
  text-align: center;
  margin-top: ${theme.spacing.lg};
  font-size: ${theme.fontSizes.small};
  
  a {
    color: ${theme.colors.primary};
    font-weight: 600;
    margin-left: ${theme.spacing.xs};
  }
`;

interface LocationState {
  from?: {
    pathname: string;
  };
}

const Login: React.FC = () => {
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  
  const { login, loading, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  
  const from = state?.from?.pathname || '/';
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    
    if (!usernameOrEmail || !password) {
      setFormError('Please enter both email/username and password');
      return;
    }
    
    try {
      console.log('Submitting login form with:', { usernameOrEmail });
      await login(usernameOrEmail, password);
      navigate(from, { replace: true });
    } catch (err: any) {
      console.error('Login form error:', err);
      setFormError(err.message || 'Failed to log in. Please check your credentials and try again.');
    }
  };
  
  return (
    <LoginContainer>
      <LoginCard>
        <LoginHeader>
          <h1>Welcome Back</h1>
          <p>Sign in to continue to The Faithful Storyteller</p>
        </LoginHeader>
        
        <LoginForm onSubmit={handleSubmit}>
          {(formError || error) && (
            <ErrorMessage>{formError || error}</ErrorMessage>
          )}
          
          <FormGroup>
            <Label htmlFor="usernameOrEmail">Email or Username</Label>
            <Input
              id="usernameOrEmail"
              type="text"
              value={usernameOrEmail}
              onChange={(e) => setUsernameOrEmail(e.target.value)}
              placeholder="Enter your email or username"
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
          </FormGroup>
          
          <ForgotPassword to="/forgot-password">Forgot password?</ForgotPassword>
          
          <Button type="submit" fullWidth disabled={loading}>
            {loading ? 'Signing In...' : 'Sign In'}
          </Button>
        </LoginForm>
        
        <SignUpLink>
          Don't have an account?
          <Link to="/register">Sign Up</Link>
        </SignUpLink>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login; 